import { useMemo } from 'react';
import { PieChart, Pie, Cell, Label } from 'recharts';
import cc from 'classcat';
import { DateTime } from 'luxon';
import { UserOrganizationPolicyDto } from '../../commonTypes/acs/user.types';
import { useTranslation } from '../../hooks/useTranslation';

type DonutProps = {
    annualColor: string;
    usedColor: string;
    carriedOverColor?: string;
    policy: UserOrganizationPolicyDto;
    cursorStyle?: 'pointer' | 'default';
    scaled?: boolean;
};

export const Donut = ({ annualColor, usedColor, carriedOverColor, policy, scaled, cursorStyle = 'default' }: DonutProps) => {
    const { t } = useTranslation('leaveRequest');

    const carriedOverLimitPassed =
        DateTime.now() > DateTime.fromObject({ year: DateTime.now().year, month: policy.carriedOverValidToMonth, day: policy.carriedOverValidToDay });

    const { data, colors } = useMemo(() => {
        if (policy.amount === 0 && policy.initialAmount === 0)
            return {
                data: [{ name: t('availableDays'), value: 1 }],
                colors: [annualColor, usedColor, carriedOverColor],
            };

        const annualDays = { name: t('annualDays'), value: Math.abs(policy.amount) };
        const usedDaysRaw = policy.initialAmount - policy.amount;
        const usedDaysWithCarriedOverRaw = usedDaysRaw + policy.initialCarriedOverAmount - policy.carriedOverAmount;
        const carriedOverDays = { name: t('carriedOverDays'), value: Math.abs(policy.carriedOverAmount) };

        const usedDays = {
            name: t('usedDays'),
            value: Math.abs(usedDaysRaw),
            isNegative: usedDaysRaw < 0,
        };
        const usedDaysWithCarriedOver = {
            name: t('usedDays'),
            value: Math.abs(usedDaysWithCarriedOverRaw),
            isNegative: usedDaysWithCarriedOverRaw < 0,
        };

        if (policy.isCarriedOver) {
            if (carriedOverLimitPassed) {
                return {
                    data: [annualDays, usedDaysWithCarriedOver],
                    colors: [annualColor, usedColor],
                };
            }
            return {
                data: [annualDays, carriedOverDays, usedDaysWithCarriedOver],
                colors: [annualColor, carriedOverColor, usedColor],
            };
        }
        return {
            data: [annualDays, usedDays],
            colors: [annualColor, usedColor],
        };
    }, [policy.amount, policy.initialAmount, policy.carriedOverAmount, policy.initialCarriedOverAmount, annualColor, usedColor, carriedOverColor]);

    return (
        <PieChart className={cc(['donut__body', scaled && 'donut__body--scaled'])} width={200} height={250} style={{ cursor: cursorStyle }}>
            <Pie
                data={data}
                dataKey="value"
                cx="50%"
                cy="60%"
                startAngle={90}
                endAngle={-270}
                blendStroke={policy.amount === policy.initialAmount}
                innerRadius={80}
                cornerRadius={50}
                paddingAngle={0}
                outerRadius="100%"
                style={{ cursor: cursorStyle }}
            >
                {data.map((item, index) => (
                    <Cell key={item.name} fill={colors[index % colors.length]} />
                ))}
            </Pie>
            <Pie data={data} dataKey="value" cx="50%" cy={'60%'} outerRadius={80} startAngle={90} endAngle={-270} style={{ cursor: cursorStyle }}>
                {data.map(item => (
                    <Cell key={item.name} fill="#EDF5FF" stroke="#EDF5FF" />
                ))}
                <Label
                    value={
                        policy.isCarriedOver && !carriedOverLimitPassed
                            ? Number((Number(policy.amount || 0) + Number(policy.carriedOverAmount || 0)).toFixed(1))
                            : Number(Number(policy.amount || 0).toFixed(1))
                    }
                    offset={0}
                    position="center"
                    className={cc(['donut__days', cursorStyle === 'pointer' && 'clickable'])}
                />

                <Label
                    value={policy.leaveType}
                    offset={0}
                    position="insideBottom"
                    className={cc(['donut__text', cursorStyle === 'pointer' && 'clickable'])}
                    width={120}
                />
            </Pie>
        </PieChart>
    );
};
